import { PropsWithChildren, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Select, Space, Typography } from 'antd';
import { NewBreadcrumbProps } from 'antd/es/breadcrumb/Breadcrumb';

import BreadCrumbs from '@/components/shared/BreadCrumbs';
import PageTitle from '@/components/shared/PageTitle';
import Wrapper from '@/components/shared/Wrapper';
import { isSubdomain } from '@/helpers/domian';
import { useRefetchWhenReachEnd } from '@/hooks/useRefetchWhenReachEnd';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';
import styled, { StyledProp } from '@/styled';

const orderMapper = [
  { order: 'ASC', orderBy: 'created_at' },
  { order: 'DESC', orderBy: 'created_at' },
  { order: 'ASC', orderBy: 'title' }
];

export type OrderParams = Omit<IPaginationParams, 'page' | 'size' | 'search'>;

interface CoursesListProps<T = any> {
  onComponentLoad: () => void;
  onReachPageEnd: (params: IPaginationParams) => void;
  onChoseFilter?: (string: OrderParams) => void;
  title: string;
  queryProps: {
    data: T;
    isFetching: boolean;
  };
  breadCrumbs?: {
    extraCrumbs?: NewBreadcrumbProps['items'];
  };
  hasFilter?: boolean;
}

function CoursesList<T extends IPaginationResponse>({
  onComponentLoad,
  onReachPageEnd,
  onChoseFilter,
  queryProps,
  breadCrumbs,
  title,
  hasFilter = true,
  children,
  ...props
}: StyledProp & PropsWithChildren<CoursesListProps<T>>) {
  const navigate = useNavigate();
  const isPortal = isSubdomain();

  const { data: response, isFetching } = queryProps;
  const orderByMenu = ['Mais recentes', 'Mais antigos', 'Alfabética A-Z'];
  if (isPortal) orderByMenu.unshift('Todos');

  const handleOrder = (order: number) => {
    if (isPortal) orderMapper.unshift(null);

    onChoseFilter(orderMapper[order]);
  };

  useRefetchWhenReachEnd<T>({ data: response, onReachedEnd: onReachPageEnd, isFetching });

  useEffect(() => {
    onComponentLoad();
  }, [onComponentLoad]);

  return (
    <Wrapper className={props.className}>
      {breadCrumbs && (
        <BreadCrumbs
          items={[
            {
              title: 'Resumo',
              onClick: () => navigate('/resumo')
            },
            ...(breadCrumbs?.extraCrumbs ?? [])
          ]}
        />
      )}
      <PageTitle
        title={title}
        className='courselist-pagetitle'
        endContent={
          hasFilter && (
            <Space size={16}>
              <Select
                disabled={isFetching}
                onChange={handleOrder}
                placeholder={<Typography.Text strong>Exibir por</Typography.Text>}
                options={orderByMenu.map((label, value) => ({ value, label }))}
                size='large'
              />
            </Space>
          )
        }
      />
      {children}
    </Wrapper>
  );
}

export default styled(CoursesList)`
  .ant-select {
    min-width: 150px;
  }

  .moreInfo {
    width: calc(100% - 16px);
    z-index: 3;
  }

  .courses-title {
    margin-top: 36px;
    font-weight: 500;
  }
`;
